/**
 * Project Writer API Client
 *
 * @description Client for calling the Project Writer Cloudflare Worker.
 * Generates AI-powered project pages from enriched gallery data.
 *
 * @architecture
 * Frontend → Project Writer Worker → OpenAI GPT-5 → Supabase project_pages
 *
 * @see /apps/production/project-writer-agent for the worker implementation
 */

/**
 * Request payload for project page generation
 */
export interface GenerateProjectRequest {
  gallery_id: number
  user_id: string
  options?: {
    /** Auto-enrich if not already done (default: true) */
    auto_enrich?: boolean
    /** Max images to include (default: 8) */
    image_count?: number
    /** Use Responses API instead of Chat Completions (default: true) */
    use_responses_api?: boolean
    /** Overwrite an existing project page for this gallery (default: false) */
    overwrite_existing?: boolean
    /** Run a pre-brief extraction step before writing (default: true) */
    prepare_brief?: boolean
    /** Max photo captions to include in the brief step (default: 8) */
    brief_image_count?: number
    /** Run a style/editor pass after drafting (default: true) */
    editor_pass?: boolean
  }
}

/**
 * Response from the project writer worker
 */
export interface GenerateProjectResponse {
  success: boolean
  project_page_id?: string
  slug?: string
  preview_url?: string
  quality_score?: number
  /** 0-100, lower = better (fewer issues found) */
  verification_score?: number
  /** True if content passed hallucination checks */
  verification_passed?: boolean
  warnings?: string[]
  error?: string
  agent_id?: string
  /** Correlates to Cloudflare logs (X-Request-Id header). */
  request_id?: string
  /** True when a background Durable Object run was started. */
  agent_started?: boolean
}

/**
 * Agent status for polling long-running operations
 */
export interface AgentStatusResponse {
  agentId: string | null
  runId: string | null
  galleryId: number | null
  userId: string | null
  status:
    | 'idle'
    | 'checking_enrichment'
    | 'enriching'
    | 'gathering_context'
    | 'preparing_brief'
    | 'generating_content'
    | 'editing_content'
    | 'validating_quality'
    | 'creating_project'
    | 'complete'
    | 'error'
  progress: number
  currentStep: string
  error: string | null
  result: {
    projectPageId: string
    slug: string
    previewUrl: string
    qualityScore: number
    warnings: string[]
  } | null
  startedAt: string | null
  completedAt: string | null
  events?: Array<{
    ts: string
    level: 'debug' | 'info' | 'warn' | 'error'
    message: string
    data?: Record<string, unknown>
  }>
  timestamp: string
}

/**
 * Get the Project Writer Worker URL and token from environment
 */
function getWorkerConfig(): { url: string; token: string } {
  const url = import.meta.env.VITE_PROJECT_WRITER_URL
  const token = import.meta.env.VITE_PROJECT_WRITER_TOKEN

  if (!url) {
    throw new Error('VITE_PROJECT_WRITER_URL environment variable is not configured')
  }
  if (!token) {
    throw new Error('VITE_PROJECT_WRITER_TOKEN environment variable is not configured')
  }

  return { url, token }
}

/**
 * Generate a project page from a gallery
 *
 * @param galleryId - The gallery ID to generate a project page from
 * @param userId - The user ID creating the project page
 * @param options - Optional configuration
 * @returns Response with project page ID and slug, or error
 *
 * @example
 * ```ts
 * const result = await generateProjectPage(123, 'user-uuid')
 * if (result.success) {
 *   console.log(`Created project page: ${result.slug}`)
 *   console.log(`Quality: ${result.quality_score}/100`)
 *   console.log(`Verification: ${result.verification_passed ? 'Passed' : 'Review needed'}`)
 * }
 * ```
 */
export async function generateProjectPage(
  galleryId: number,
  userId: string,
  options?: GenerateProjectRequest['options']
): Promise<GenerateProjectResponse> {
  const { url, token } = getWorkerConfig()

  try {
    const response = await fetch(`${url}/api/generate-project`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        gallery_id: galleryId,
        user_id: userId,
        options,
      } satisfies GenerateProjectRequest),
    })

    const data: GenerateProjectResponse = await response.json()
    const requestId = response.headers.get('X-Request-Id') ?? undefined

    if (!response.ok && !data.error) {
      return {
        success: false,
        error: `HTTP ${response.status}: ${response.statusText}`,
      }
    }

    return { ...data, request_id: requestId ?? data.request_id }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Network error'
    console.error('[generateProjectPage] Error:', errorMessage)

    return {
      success: false,
      error: `Failed to connect to project writer: ${errorMessage}`,
    }
  }
}

/**
 * Check the status of a running project generation
 *
 * @param agentId - The agent ID returned from generateProjectPage
 * @returns Current status of the agent
 *
 * @example
 * ```ts
 * const status = await getAgentStatus('gallery-123-1234567890')
 * if (status.status === 'complete') {
 *   console.log(`Done! Preview at: ${status.result?.previewUrl}`)
 * }
 * ```
 */
export async function getAgentStatus(agentId: string): Promise<AgentStatusResponse | null> {
  const { url, token } = getWorkerConfig()

  try {
    const response = await fetch(`${url}/api/status/${agentId}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      console.error('[getAgentStatus] HTTP error:', response.status)
      return null
    }

    return await response.json()
  } catch (error) {
    console.error('[getAgentStatus] Error:', error)
    return null
  }
}

/**
 * Health check for the project writer worker
 *
 * @returns True if the worker is healthy
 */
export async function checkWorkerHealth(): Promise<boolean> {
  try {
    const { url } = getWorkerConfig()
    const response = await fetch(`${url}/health`)
    return response.ok
  } catch {
    return false
  }
}

/**
 * Format quality score for display
 */
export function formatQualityScore(score: number): {
  label: string
  color: 'green' | 'yellow' | 'red'
  emoji: string
} {
  if (score >= 80) {
    return { label: 'Excellent', color: 'green', emoji: '🟢' }
  } else if (score >= 70) {
    return { label: 'Good', color: 'green', emoji: '🟢' }
  } else if (score >= 60) {
    return { label: 'Needs Review', color: 'yellow', emoji: '🟡' }
  } else {
    return { label: 'Low Quality', color: 'red', emoji: '🔴' }
  }
}

/**
 * Format verification status for display
 */
export function formatVerificationStatus(
  passed: boolean,
  score: number
): {
  label: string
  color: 'green' | 'yellow' | 'red'
  description: string
} {
  if (passed) {
    return {
      label: 'Verified',
      color: 'green',
      description: 'Content is fact-based and traces to job data',
    }
  } else if (score < 50) {
    return {
      label: 'Minor Issues',
      color: 'yellow',
      description: 'Some content may need human review',
    }
  } else {
    return {
      label: 'Review Required',
      color: 'red',
      description: 'Content may contain fabricated details',
    }
  }
}
